// import cn from 'classnames';
import { useMemo, useCallback, useState, ReactElement } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { add, differenceInSeconds } from 'date-fns';
import i18n from "i18next";
import CopyTextToClipboard from '@components/CopyTextToClipboard';
import { Button, ButtonSize, ButtonType } from "@components/new/Button";
import { LinkButton, LinkButtonSize } from "@components/new/LinkButton";
import { IconButton, IconButtonSize, IconButtonType } from "@components/new/IconButton";
import ButtonWithTimer from '@components/new/ButtonWithTimer/ButtonWithTimer';
import DataTable from '@containers/DataTableNew';
import { Filters, FilterTypes } from '@containers/FiltersNew';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import {
  getFBPRequests,
  getFBPRequestSKULabel,
  cancelFBPRestockRequest,
  TRestoreRequestsPageItem,
  TRequestParams,
  TWarehouse,
  TSellerWarehouse,
} from "@api/apiClient/fbp";
import { Tooltip } from '@components/new/Tooltip';
import ConfirmModal from '@components/modals/ConfirmModal';
import { TableColumn } from "@components/new/Table";
import OrderModal from "../modals/OrderModal";
import PickUpModal from "../modals/PickUpModal";
import { weightGValue, weightKgValue, formatDate, formatNumber } from "@utils/formatValues";
import StatusBadge from "../components/StatusBadge";
import PickUpStatusBadge from "../components/PickUpStatusBadge";
import { useKnowledgeBase } from '@contexts/KnowledgeBaseContext';
import InfoIcon from '@assets/icons/outline/info.svg?react';
import DownloadIcon from '@assets/icons/outline/download.svg?react';
import IconPlus from '@assets/icons/outline/plus.svg?react';
import TruckIcon from '@assets/icons/outline/truck.svg?react';
import styles from './Tab.module.scss';

const OrdersTabDelivery = ({ sellerWarehouse, activeWarehouse, openCreateOrderModal }: {
  sellerWarehouse: TSellerWarehouse | undefined;
  openCreateOrderModal: () => void;
  activeWarehouse: TWarehouse | undefined;
}): ReactElement => {
  const fbpWarehouseId = activeWarehouse?.id ?? "";
  const isPickUpAvailable = activeWarehouse?.isPickUpAvailable ?? false;
  const warehouseAddress = activeWarehouse?.address ?? "";
  const warehousePhone = activeWarehouse?.contactPhone ?? "";
  const sellerWarehouseId = sellerWarehouse?.id ?? "";

  const { t } = useTranslation();
  const currentLanguage = i18n.language;
  const queryClient = useQueryClient();
  const [isOpenOrderModal, setOpenOrderModal] = useState<boolean>(false);
  const [isOpenCancelWindow, setIsOpenCancelWindow] = useState<boolean>(false);
  const [currentRequestId, setCurrentRequestId] = useState<string>("");
  const [isPickUpModalOpen, setPickUpModalOpen] = useState<boolean>(false);
  // const [pickUpData, setPickUpData] = useState<TRestoreRequestsPageItem | null>(null);
  const [pickUpId, setPickUpId] = useState<string>("");
  const [cancelRequest, setCancelRequest] = useState<TRestoreRequestsPageItem | undefined>(undefined);
  const [sorting, setSorting] = useState<{ column: string, direction: string } | undefined>(undefined);
  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const { setKnowledgeBaseParams } = useKnowledgeBase();

  const downloadRequestLabelsMutation = useMutation({
    mutationFn: (id: string) => getFBPRequestSKULabel({ id }),
    onSuccess: ({ data, filename }) => {
      const file = new Blob([data], { type: 'application/zip' });
      const fileURL = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = fileURL;
      a.download = filename;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(fileURL), 100);
    },
  });

  const cancelRequestMutation = useMutation({
    mutationFn: (id: string) => cancelFBPRestockRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries(
        { queryKey: ["getFBPRequests"] },
      );
    },
  });

  const columns: TableColumn<TRestoreRequestsPageItem>[] = useMemo(() => [
    {
      header: t("fbpPage.ordersTab.columns.number"),
      renderCell: (row) => (
        <CopyTextToClipboard
          text={row.data?.number}
          visibleOnHover
        >
          {row.data?.number}
        </CopyTextToClipboard>
      ),
      width: "160px",
      // fixWidth: true,
    },
    {
      id: "count",
      header: t("fbpPage.ordersTab.columns.count"),
      valueGetter: (data: TRestoreRequestsPageItem) => (data.ymProductsCount + data.wbProductsCount + data.aliProductsCount),
      // alignment: "center",
      width: "160px",
      fixWidth: true,
      // sortable: true,
    },
    {
      id: "weight",
      header: t("fbpPage.ordersTab.columns.weight"),
      valueGetter: (data: TRestoreRequestsPageItem) => {
        const value = (weightGValue(data.wbTotalWeight) + weightGValue(data.ymTotalWeight) + weightGValue(data.aliTotalWeight)) / 1000;
        return value ? formatNumber(value) : "0";
      },
      // alignment: "center",
      width: "160px",
      fixWidth: true,
      // sortable: true,
    },
    {
      id: "createdAt",
      header: t("fbpPage.ordersTab.columns.createdAt"),
      valueGetter: (data: TRestoreRequestsPageItem) => formatDate(data.createdAt, 'dd.MM.yy'),
      // alignment: "center",
      width: "160px",
      fixWidth: true,
      // sortable: true,
    },
    {
      header: t("fbpPage.ordersTab.columns.status"),
      renderCell: (row) => (<StatusBadge value={row.data.status} />),
      // alignment: "center",
      width: "160px",
      fixWidth: true,
    },
    {
      header: "",
      renderCell: (row) => {
        // if (row.data.isCancelable && isFuture(new Date(endDate))) {
        if (row.data.isCancelable) {
          const endDate = add(row.data.createdAt, { hours: 1 });
          const timeout = differenceInSeconds(new Date(endDate), new Date());

          if (timeout > 0) {
            return (
              <ButtonWithTimer
                key={`${row.data.id}-${row.data.createdAt}`}
                type={ButtonType.SECONDARY}
                onClick={() => {
                  setIsOpenCancelWindow(true);
                  setCancelRequest(row.data);
                }}
                pending={cancelRequestMutation.isLoading}
                title={t("fbpPage.ordersTab.cancelBtn")}
                timeout={timeout > 0 ? timeout : 0}
                startTimerFromOutside
                isWide
              />
            );
          }

          return (
            <Button
              type={ButtonType.SECONDARY}
              onClick={() => {
                setIsOpenCancelWindow(true);
                setCancelRequest(row.data);
              }}
              loading={cancelRequestMutation.isLoading}
              title={t("fbpPage.ordersTab.cancelBtn")}
              size={ButtonSize.S}
              isWide
            />
          );
        }

        if (["Accepted", "WarehouseReceiving", "PartiallyCompleted", "OnWay"].find(v => (v === row.data.status))) {
          return (
            <Button
              size={ButtonSize.S}
              type={ButtonType.PRIMARY}
              icon={DownloadIcon}
              onClick={() => downloadRequestLabelsMutation.mutate(row.data.id)}
              loading={downloadRequestLabelsMutation.isLoading}
              title={t("fbpPage.ordersTab.labelsBtn")}
              isWide
            />
          );
        }

        return null;
      },
      // alignment: "center",
      width: "200px",
      fixWidth: true,
    },
    ...(isPickUpAvailable ? [{
      header: "Pick up",
      renderCell: (row) => {
        if (row.data.shipmentDate) {
            return (
                <PickUpStatusBadge value={row.data.pickUpStatus} />
            )
        }

        const totalWeight = (weightKgValue(row.data.wbTotalWeight) + weightKgValue(row.data.ymTotalWeight) + weightKgValue(row.data.aliTotalWeight));
        const btnStatus = {
          disabled: false,
          tooltip: t("fbpPage.pickUp.tooltips.freePickup"),
        };
        if (!["Accepted", "Created", "OnWay"].find(v => (v === row.data.status))) {
          btnStatus.disabled = true;
          btnStatus.tooltip = t("fbpPage.pickUp.tooltips.parcelAccepted");
        } else if (totalWeight < 10 || totalWeight > 3000) {
          btnStatus.disabled = true;
          btnStatus.tooltip = t("fbpPage.pickUp.tooltips.pickupUnavailable");
        }

        if (!row.data.isPickUpAvailable) return null;

        return (
          <Tooltip
            action={
              <IconButton
                size={IconButtonSize.M}
                type={IconButtonType.PRIMARY}
                icon={TruckIcon}
                onClick={() => {
                  setPickUpModalOpen(true);
                  setPickUpId(row.data?.id ?? "");
                }}
                disabled={btnStatus.disabled}
              />
            }
            position="top"
            align="start"
            text={btnStatus.tooltip}
          />
        );
      },
      alignment: "center",
      width: "200px",
      fixWidth: true,
    }] as TableColumn<TRestoreRequestsPageItem>[] : []),
  ], [currentLanguage, isPickUpAvailable]);

  const ordersGetter = useCallback(
    ({ filters, sorting, page }: TRequestParams) => getFBPRequests({ filters, sorting, page, sellerWarehouseId }),
    [sellerWarehouseId]
  );

  return (
    <div className={styles.tabContent}>
      <div className={styles.actionsRow}>
        <Filters
          id="fbp-orders"
          // openFilters={() => setIsFilterOpen(true)}
          items={[
            {
              name: 'numbers',
              type: FilterTypes.MULTI_SEARCH_NEW,
              placeholder: t('fbpPage.requestSearch'),
            },
          ]}
          onChange={(v) => {
            setInnerFilters(v);
          }}
          values={innerFilters}
          // asPanel
        />

        <div className={styles.actionsRight}>
          <LinkButton
            icon={InfoIcon}
            size={LinkButtonSize.L}
            title={t('fbpPage.instructions')}
            onClick={() => setKnowledgeBaseParams(`https://unitrade-global.com/page139704916.html`)}
          />
          <Button
            size={ButtonSize.M}
            onClick={openCreateOrderModal}
            title={t('fbpPage.addOrderBtn')}
            icon={IconPlus}
            disabled={sellerWarehouse?.isAwaitingDeletion}
          />
        </div>
      </div>

      <DataTable<TRestoreRequestsPageItem>
        key={sellerWarehouseId}
        columns={columns}
        filters={innerFilters}
        queryKey={["getFBPRequests", sellerWarehouseId]}
        getData={ordersGetter}
        rowOnClick={(row) => {
          setCurrentRequestId(row.id)
          setOpenOrderModal(true);
        }}
        verticalAlignment="top"
        noLoaderOnRefresh
        noDataProps={{
          title: t("fbpPage.noOrdersTitle"),
          btnTitle: t('fbpPage.noItemsBtn'),
          onClick: openCreateOrderModal,
          width: "max-content",
          btnDisabled: sellerWarehouse?.isAwaitingDeletion,
        }}
        pending={!sellerWarehouseId}
        sorting={sorting}
        onSortingChange={(column, direction) => setSorting({ column, direction })}
      />
      {isOpenOrderModal ? (
        <OrderModal
          onClose={() => setOpenOrderModal(false)}
          sellerWarehouseId={sellerWarehouseId}
          fbpWarehouseId={fbpWarehouseId}
          isPickUpAvailable={isPickUpAvailable}
          id={currentRequestId}
          warehouseAddress={warehouseAddress}
          warehousePhone={warehousePhone}
          createPickUp={(id: string) => {
            setOpenOrderModal(false);
            setPickUpId(id);
            setPickUpModalOpen(true);
          }}
        />
      ) : null}

      {isOpenCancelWindow && (
        <ConfirmModal
          key={cancelRequest?.id}
          title={cancelRequest?.shipmentDate
            ? t("fbpPage.cancelRequestModal.pickUpTitle")
            : t("fbpPage.cancelRequestModal.title")}
          description={cancelRequest?.shipmentDate
            ? t("fbpPage.cancelRequestModal.pickUpDescription")
            : t("fbpPage.cancelRequestModal.description", { number: cancelRequest?.number })}
          noBtnTitle={t("fbpPage.cancelRequestModal.cancelBtn")}
          yesBtnTitle={t("fbpPage.cancelRequestModal.okBtn")}
          onClose={() => setIsOpenCancelWindow(false)}
          requestFn={() => cancelRequestMutation.mutate(cancelRequest?.id ?? "")}
          onSuccess={() => setIsOpenCancelWindow(false)}
        />
      )}

      {isPickUpModalOpen && (
        <PickUpModal
          onClose={() => setPickUpModalOpen(false)}
          // sellerWarehouseId={sellerWarehouseId}
          restockRequestId={pickUpId}
          fbpWarehouseId={fbpWarehouseId}
        />
      )}
    </div>
  );
};

export default OrdersTabDelivery;
